/**
 * Layout breakpoints (CSS can't share variables in media queries — keep these numbers in sync with styles.css).
 *   phone            < 700px
 *   tablet portrait  700–1023px   (iPad mini 744, iPad / Air 820, Pro 11" 834; portrait iPads, best effort)
 *   wide             >= 1024px    (iPad 9.7" landscape 1024, the design target; iPad landscape 1133–1376,
 *                                  iPad Pro 13" portrait 1032, desktop)
 * The navigation alone also changes at 1440px (the rail can collapse to icons, with a mouse) and at 1600px (the
 * labelled sidebar).
 * Touch sizing keys off `(pointer: coarse)`, hover effects off `(hover: hover)` — not off width.
 */
import { useSyncExternalStore } from 'react';

export const BREAKPOINTS = {
  tablet: 700,
  wide: 1024,
  collapsibleNav: 1440,
} as const;

export const MQ = {
  tabletUp: `(min-width: ${BREAKPOINTS.tablet}px)`,
  wideUp: `(min-width: ${BREAKPOINTS.wide}px)`,
  /** Where the rail's collapse toggle shows: its names live in tooltips, so it needs a pointer that can hover. */
  collapsibleNav: `(min-width: ${BREAKPOINTS.collapsibleNav}px) and (hover: hover)`,
  landscape: '(orientation: landscape)',
  coarse: '(pointer: coarse)',
} as const;

/** Reactive `window.matchMedia(query).matches` (false during SSR/tests without window). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  );
}
